import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  Assessment as AssessmentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { ImportWizard } from '../import';
import { customersApi, getErrorMessage } from '../../api/services';
import { Customer, CustomerListResponse } from '../../types/api';

interface CustomerSummary {
  total_customers: number;
  active_customers: number;
  total_revenue: number;
  outstanding_balance: number;
  total_credit_limit?: number;
  average_payment_terms?: number;
}

interface CustomerExtended extends Customer {
  credit_limit_formatted?: string;
  analytics?: {
    total_revenue: number;
    outstanding_amount: number;
    payment_score: number;
    risk_level: string;
    credit_utilization: number;
    first_invoice_date?: string;
    last_invoice_date?: string;
    total_invoices: number;
    average_invoice_value: number;
    payment_history: Array<{
      month: string;
      invoices_count: number;
      total_amount: number;
      paid_on_time: number;
    }>;
  };
}

const CustomerManagement: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState<CustomerSummary | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerExtended | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch customers from API
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await customersApi.getAll({ search: searchTerm });
      setCustomers(data.customers);
      setSummary(data.summary);
      setError(null);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setSnackbar({ open: true, message: 'Failed to load customers', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [searchTerm]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomer(null);
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'low': return <CheckCircleIcon fontSize="small" />;
      case 'medium': return <WarningIcon fontSize="small" />;
      case 'high': return <WarningIcon fontSize="small" />;
      default: return <CheckCircleIcon fontSize="small" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleViewCustomer = () => {
    if (selectedCustomer) {
      setFormData(selectedCustomer);
      setShowViewDialog(true);
    }
    handleMenuClose();
  };

  const handleEditCustomer = () => {
    if (selectedCustomer) {
      console.log('Setting form data for edit:', selectedCustomer);
      // Store the customer data for editing
      setFormData({ ...selectedCustomer });
      setShowEditDialog(true);
    }
    handleMenuClose();
  };

  const handleDeleteCustomer = () => {
    if (selectedCustomer) {
      setShowDeleteDialog(true);
    }
    handleMenuClose();
  };

  const handleAddCustomer = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      street_name: '',
      town_city: '',
      postcode: '',
      tax_id: '',
      credit_limit: 5000,
      payment_terms: 30,
      is_active: true
    });
    setShowAddDialog(true);
  };

  const saveCustomer = async () => {
    try {
      console.log('Creating customer with data:', formData);
      const newCustomer = await customersApi.create(formData as any);
      console.log('Customer created successfully:', newCustomer);
      setSnackbar({ open: true, message: 'Customer created successfully!', severity: 'success' });
      setShowAddDialog(false);
      fetchCustomers(); // Refresh the list
    } catch (err) {
      console.error('Create error:', err);
      const errorMessage = getErrorMessage(err);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const updateCustomer = async () => {
    console.log('updateCustomer function called!');
    console.log('selectedCustomer:', selectedCustomer);
    console.log('formData.id:', formData.id);

    const customerId = formData.id || selectedCustomer?.id;

    if (!customerId) {
      console.log('No customer ID available, returning early');
      return;
    }

    try {
      // Clean the form data - only send fields that the backend expects
      const cleanData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        building_number: formData.building_number,
        building_name: formData.building_name,
        street_name: formData.street_name,
        locality: formData.locality,
        town_city: formData.town_city,
        county: formData.county,
        postcode: formData.postcode,
        tax_id: formData.tax_id,
        company_number: formData.company_number,
        credit_limit: formData.credit_limit,
        payment_terms: formData.payment_terms,
        is_active: formData.is_active
      };

      // Remove undefined/null values and internal fields
      const updateData = Object.fromEntries(
        Object.entries(cleanData).filter(([key, value]) =>
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !key.startsWith('_') // Exclude internal fields like _editingCustomerId
        )
      );

      console.log('Original form data:', formData);
      console.log('Cleaned update data:', updateData);
      console.log('Updating customer ID:', customerId);

      const updatedCustomer = await customersApi.update(customerId, updateData);
      console.log('Customer updated successfully:', updatedCustomer);
      setSnackbar({ open: true, message: 'Customer updated successfully!', severity: 'success' });
      setShowEditDialog(false);

      // Force refresh the customer list
      setTimeout(() => {
        fetchCustomers();
      }, 100);
    } catch (err) {
      console.error('Update error:', err);
      const errorMessage = getErrorMessage(err);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const deleteCustomer = async () => {
    if (!selectedCustomer?.id) return;

    try {
      await customersApi.delete(selectedCustomer.id);
      setSnackbar({ open: true, message: 'Customer deleted successfully!', severity: 'success' });
      setShowDeleteDialog(false);
      fetchCustomers(); // Refresh the list
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading customers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Customer Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setShowImportDialog(true)}
          >
            Import Customers
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCustomer}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Customers
                    </Typography>
                    <Typography variant="h5">
                      {summary.total_customers}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Active Customers
                    </Typography>
                    <Typography variant="h5">
                      {summary?.active_customers || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CreditCardIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Credit Limit
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(summary?.total_credit_limit || 0)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Avg Payment Terms
                    </Typography>
                    <Typography variant="h5">
                      {summary.average_payment_terms} days
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search customers by name, email, Tax ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Customers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Credit Limit</TableCell>
              <TableCell>Payment Score</TableCell>
              <TableCell>Risk Level</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <TableRow key={customer.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">
                      {customer.name}
                    </Typography>
                    {customer.tax_id && (
                      <Typography variant="caption" color="textSecondary">
                        Tax ID: {customer.tax_id}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {customer.email}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {customer.phone}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {customer.town_city}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {customer.postcode}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {customer.credit_limit_formatted}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {customer.analytics?.credit_utilization?.toFixed(1) || '0.0'}% used
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">
                      {customer.analytics?.payment_score || 0}/100
                    </Typography>
                    <TrendingUpIcon
                      fontSize="small"
                      sx={{ ml: 1, color: (customer.analytics?.payment_score || 0) >= 80 ? 'success.main' : 'warning.main' }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getRiskLevelIcon(customer.analytics?.risk_level || 'Low')}
                    label={customer.analytics?.risk_level || 'Low'}
                    color={getRiskLevelColor(customer.analytics?.risk_level || 'Low')}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={customer.is_active ? 'Active' : 'Inactive'}
                    color={customer.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, customer)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {customers.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No customers found
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first customer'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCustomer}
          >
            Add Customer
          </Button>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewCustomer}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditCustomer}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Customer
        </MenuItem>
        <MenuItem onClick={handleDeleteCustomer} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Customer
        </MenuItem>
      </Menu>

      {/* Add Customer Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="Company Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Tax ID"
              value={formData.tax_id || ''}
              onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="Street Name"
              value={formData.street_name || ''}
              onChange={(e) => setFormData({ ...formData, street_name: e.target.value })}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Town/City"
              value={formData.town_city || ''}
              onChange={(e) => setFormData({ ...formData, town_city: e.target.value })}
              fullWidth
            />
            <TextField
              label="Postcode"
              value={formData.postcode || ''}
              onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
              fullWidth
            />
            <TextField
              label="Credit Limit ($)"
              type="number"
              value={formData.credit_limit || 5000}
              onChange={(e) => setFormData({ ...formData, credit_limit: Number(e.target.value) })}
              fullWidth
            />
            <TextField
              label="Payment Terms (days)"
              type="number"
              value={formData.payment_terms || 30}
              onChange={(e) => setFormData({ ...formData, payment_terms: Number(e.target.value) })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveCustomer}>Add Customer</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="Company Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Tax ID"
              value={formData.tax_id || ''}
              onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="Street Name"
              value={formData.street_name || ''}
              onChange={(e) => setFormData({ ...formData, street_name: e.target.value })}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Town/City"
              value={formData.town_city || ''}
              onChange={(e) => setFormData({ ...formData, town_city: e.target.value })}
              fullWidth
            />
            <TextField
              label="ZIP Code"
              value={formData.postcode || ''}
              onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
              fullWidth
            />
            <TextField
              label="Credit Limit ($)"
              type="number"
              value={formData.credit_limit || 5000}
              onChange={(e) => setFormData({ ...formData, credit_limit: Number(e.target.value) })}
              fullWidth
            />
            <TextField
              label="Payment Terms (days)"
              type="number"
              value={formData.payment_terms || 30}
              onChange={(e) => setFormData({ ...formData, payment_terms: Number(e.target.value) })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
          <Button onClick={() => console.log('Current form data:', formData)} color="info">Debug Form Data</Button>
          <Button variant="contained" onClick={() => {
            console.log('Update Customer button clicked!');
            updateCustomer();
          }}>Update Customer</Button>
        </DialogActions>
      </Dialog>

      {/* View Customer Dialog */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Customer Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">Company Name</Typography>
              <Typography variant="body1">{formData.name}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">Email</Typography>
              <Typography variant="body1">{formData.email}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
              <Typography variant="body1">{formData.phone || 'Not provided'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">Tax ID</Typography>
              <Typography variant="body1">{formData.tax_id || 'Not provided'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="textSecondary">Street Name</Typography>
              <Typography variant="body1">{formData.street_name || 'Not provided'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">Town/City</Typography>
              <Typography variant="body1">{formData.town_city}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">ZIP Code</Typography>
              <Typography variant="body1">{formData.postcode}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">Credit Limit</Typography>
              <Typography variant="body1">${formData.credit_limit?.toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">Payment Terms</Typography>
              <Typography variant="body1">{formData.payment_terms} days</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewDialog(false)}>Close</Button>
          <Button variant="contained" onClick={() => {
            setShowViewDialog(false);
            setShowEditDialog(true);
          }}>Edit Customer</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedCustomer?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteCustomer}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Import Wizard */}
      <ImportWizard
        open={showImportDialog}
        onClose={() => {
          setShowImportDialog(false);
          fetchCustomers(); // Refresh customer list after import
        }}
        entityType="customer"
        onImportComplete={(result) => {
          setSnackbar({
            open: true,
            message: `Successfully imported ${result.imported_count} customers!`,
            severity: 'success'
          });
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerManagement;
